/**
 * When the explorer home is allowed to load Google Maps.
 *
 * Maps JS (plus our map component, supercluster, overlay layers) was the
 * single biggest chunk of work on first load, yet the map sits under a hero
 * and most visits never touch it before scrolling. The split map now mounts
 * only once ONE of these is true (and then stays mounted — the latch lives in
 * the hook):
 *
 *  - the user showed intent: a search ran, a region/route/trip is active, a
 *    drop-a-pin is armed, they pressed Map + List / Plan a road trip, or the
 *    URL deep-links to something that frames the map (see hasMapIntentInUrl);
 *  - the map container is near the viewport AND the page has settled (first
 *    user input, or a short idle period after `load`). The second half keeps
 *    Maps off the critical path when the shorter hero puts the map in the
 *    first screen.
 *
 * The fullscreen "Full Map" view is not gated — opening it IS the intent.
 */
import { useEffect, useState, type RefObject } from 'react';

export interface MapGateInput {
  /** Explicit or derived intent to use the map right now. */
  userIntent: boolean;
  /** Map container is within the IntersectionObserver rootMargin. */
  nearViewport: boolean;
  /** First input happened, or the page has been idle for a beat after load. */
  pageSettled: boolean;
}

export function shouldActivateExplorerMap({ userIntent, nearViewport, pageSettled }: MapGateInput): boolean {
  if (userIntent) return true;
  return nearViewport && pageSettled;
}

/**
 * Deep links whose whole point is the map — they must not wait for a scroll.
 * Mirrors the params HomeClient's URL effect acts on.
 */
const MAP_INTENT_PARAMS = [
  'plan', // /trip-planner → ?plan=1
  'region',
  'location',
  'search',
  'rerun',
  'startPoint',
  'endPoint',
  'isRouteSearch',
] as const;

export function hasMapIntentInUrl(params: { get(name: string): string | null } | null | undefined): boolean {
  if (!params) return false;
  return MAP_INTENT_PARAMS.some((key) => {
    const value = params.get(key);
    return value !== null && value.trim() !== '' && value !== '0' && value !== 'false';
  });
}

/** Viewport rootMargin: start loading a little before the map scrolls in. */
export const MAP_NEAR_VIEWPORT_MARGIN = '300px';

/** Latches true once `ref` comes within `rootMargin` of the viewport. */
export function useNearViewport(ref: RefObject<Element>, rootMargin: string = MAP_NEAR_VIEWPORT_MARGIN): boolean {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el || typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      // No element yet / no IO support: don't block the map forever.
      if (typeof window !== 'undefined' && typeof IntersectionObserver === 'undefined') setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, near]);
  return near;
}

/**
 * True after the first real user input (pointer/key/touch/wheel/scroll), or
 * `idleDelayMs` after the page `load` event + an idle callback — whichever is
 * first. Lighthouse never interacts, so this mostly keeps Maps out of TTI.
 */
export function usePageSettled(idleDelayMs: number = 1200): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (settled || typeof window === 'undefined') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let idleHandle: number | null = null;
    const done = () => {
      if (!cancelled) setSettled(true);
    };
    const inputEvents = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;
    inputEvents.forEach((evt) => window.addEventListener(evt, done, { once: true, passive: true }));

    const afterLoad = () => {
      const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (ric) {
        idleHandle = ric(() => {
          timer = setTimeout(done, idleDelayMs);
        }, { timeout: 2000 });
      } else {
        timer = setTimeout(done, idleDelayMs + 300);
      }
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });

    return () => {
      cancelled = true;
      inputEvents.forEach((evt) => window.removeEventListener(evt, done));
      window.removeEventListener('load', afterLoad);
      if (timer) clearTimeout(timer);
      const cic = (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (idleHandle !== null && cic) cic(idleHandle);
    };
  }, [settled, idleDelayMs]);
  return settled;
}

/** Tracks a CSS media query (false during SSR / first render). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}
